import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Building2,
  CheckCircle2,
  Clock,
  MapPin,
  Phone,
  Sparkles,
  User,
} from 'lucide-react';
import {
  BusinessHoursInput,
  Button,
  DocumentInput,
  Logo,
  PhoneInput,
  RichTextInput,
  SearchableComboBox,
  Stepper,
  TextInput,
  WEEK_DAYS,
  defaultWeekHours,
  isWeekHoursComplete,
  weekHoursToPayload,
} from '@kardume/ui';
import type { DayHours, StepperStep, WeekHours } from '@kardume/ui';
import { extractErrorMessage, onboardingApi } from '@kardume/services';
import type {
  ComboItem,
  DayOfWeek,
  MerchantOnboardingRequestDto,
  MerchantOnboardingResponseDto,
  PublicCategoryDto,
} from '@kardume/types';
import { BUSINESS_HOUR_ERROR_MESSAGES } from '@kardume/types';

const UF_OPTIONS: ComboItem[] = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
].map((uf) => ({ id: uf, label: uf }));

const DOC_TYPE_OPTIONS: ComboItem[] = [
  { id: 'CNPJ', label: 'CNPJ' },
  { id: 'CPF', label: 'CPF' },
];

const STEPS: StepperStep[] = [
  { id: 'store', label: 'Loja' },
  { id: 'responsible', label: 'Responsável' },
  { id: 'address', label: 'Endereço' },
  { id: 'hours', label: 'Horários' },
  { id: 'plan', label: 'Plano' },
  { id: 'review', label: 'Revisão' },
];

type DocumentType = 'CPF' | 'CNPJ';

interface OnboardingForm {
  tradeName: string;
  name: string;
  documentType: DocumentType;
  document: string;
  categoryId: string;
  description: string;
  email: string;
  phone: string;
  whatsapp: string;
  website: string;
  instagram: string;
  responsibleName: string;
  responsibleRole: string;
  responsibleEmail: string;
  responsiblePhone: string;
  responsibleDocument: string;
  zipCode: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  hours: WeekHours;
}

const emptyForm = (): OnboardingForm => ({
  tradeName: '',
  name: '',
  documentType: 'CNPJ',
  document: '',
  categoryId: '',
  description: '',
  email: '',
  phone: '',
  whatsapp: '',
  website: '',
  instagram: '',
  responsibleName: '',
  responsibleRole: '',
  responsibleEmail: '',
  responsiblePhone: '',
  responsibleDocument: '',
  zipCode: '',
  street: '',
  number: '',
  complement: '',
  district: '',
  city: '',
  state: '',
  hours: defaultWeekHours(),
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** One line per day for the review step: "08:00 às 12:00, 13:30 às 19:00". */
const describeDayHours = (day: DayHours): string =>
  day.closed
    ? 'Fechado'
    : day.windows.map((window) => `${window.opensAt || '--:--'} às ${window.closesAt || '--:--'}`).join(', ') ||
      '-';

const maskCep = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
};

/** Friendly Portuguese copy for the business errors raised by the core. */
const SUBMIT_ERROR_MESSAGES: Record<string, string> = {
  MERCHANT_DOCUMENT_EXISTS:
    'Já existe um cadastro com esse CNPJ/CPF. Se a loja é sua, entre em contato com o time Kardume.',
  ...BUSINESS_HOUR_ERROR_MESSAGES,
};

const Screen = styled.div`
  min-height: 100vh;
  width: 100%;
  padding: 2.5rem 1.25rem 3rem;
  box-sizing: border-box;
  background-color: ${(p) => p.theme.background};
  background-image: radial-gradient(at 0% 0%, ${(p) => p.theme.primary}14 0, transparent 50%),
    radial-gradient(at 100% 0%, ${(p) => p.theme.petrol}14 0, transparent 50%),
    radial-gradient(at 50% 100%, ${(p) => p.theme.accent}0f 0, transparent 50%);
`;

const Shell = styled.div`
  width: 100%;
  max-width: 880px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Intro = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-align: center;

  h1 {
    margin: 0;
    font-size: 1.6rem;
    color: ${(p) => p.theme.textPrimary};
  }
  p {
    margin: 0;
    max-width: 560px;
    font-size: 0.92rem;
    line-height: 1.55;
    color: ${(p) => p.theme.textSecondary};
  }
`;

const Panel = styled.section`
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  box-shadow: ${(p) => p.theme.shadowMd};
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 1rem;
  color: ${(p) => p.theme.textPrimary};
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textMuted};
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const Grid3 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.85rem;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const DocGrid = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 0.85rem;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const InfoBox = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px dashed ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  background: ${(p) => p.theme.surfaceAlt};

  svg {
    flex: 0 0 auto;
    color: ${(p) => p.theme.accent};
  }

  div {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  strong {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }
  span {
    font-size: 0.85rem;
    line-height: 1.5;
    color: ${(p) => p.theme.textSecondary};
  }
`;

const ReviewList = styled.dl`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 0.55rem 1rem;
  margin: 0;

  dt {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: ${(p) => p.theme.textMuted};
  }
  dd {
    margin: 0;
    font-size: 0.88rem;
    color: ${(p) => p.theme.textPrimary};
    overflow-wrap: anywhere;
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
    gap: 0.15rem 0;

    dd {
      margin-bottom: 0.6rem;
    }
  }
`;

const ReviewGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 1rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  background: ${(p) => p.theme.surfaceAlt};
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  border-top: 1px solid ${(p) => p.theme.border};
  padding-top: 1rem;
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${(p) => p.theme.error};
`;

const Success = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.85rem;
  text-align: center;
  padding: 1rem 0;

  svg {
    color: ${(p) => p.theme.success};
  }
  h2 {
    margin: 0;
    font-size: 1.3rem;
    color: ${(p) => p.theme.textPrimary};
  }
  p {
    margin: 0;
    max-width: 520px;
    font-size: 0.92rem;
    line-height: 1.6;
    color: ${(p) => p.theme.textSecondary};
  }
  code {
    font-size: 0.8rem;
    padding: 0.25rem 0.5rem;
    border-radius: ${(p) => p.theme.radius};
    background: ${(p) => p.theme.surfaceAlt};
    color: ${(p) => p.theme.textSecondary};
  }
`;

type FieldErrors = Partial<Record<keyof OnboardingForm | 'hours', string>>;

/**
 * Public self-service merchant onboarding wizard.
 *
 * Submits to `POST /v1/onboarding/merchant` (public endpoint - see
 * `app.security.permit-all-paths` in the core), which creates a PENDING lead
 * for the backoffice to review. The merchant has no account at this point, so
 * the screen renders outside the authenticated layout.
 */
const MerchantOnboardingPage: React.FC = () => {
  const [step, setStep] = useState<number>(0);
  const [form, setForm] = useState<OnboardingForm>(emptyForm());
  const [errors, setErrors] = useState<FieldErrors>({});
  const [categories, setCategories] = useState<PublicCategoryDto[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(true);
  const [cepLoading, setCepLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<MerchantOnboardingResponseDto | null>(null);

  useEffect(() => {
    let active = true;
    onboardingApi
      .listCategories()
      .then((list) => {
        if (active) setCategories(list);
      })
      .catch(() => {
        if (active) setCategories([]);
      })
      .finally(() => {
        if (active) setCategoriesLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const categoryOptions = useMemo<ComboItem[]>(
    () => categories.map((category) => ({ id: category.id, label: category.name })),
    [categories]
  );
  const categoryName = categories.find((category) => category.id === form.categoryId)?.name ?? '-';

  const set = <K extends keyof OnboardingForm>(key: K, value: OnboardingForm[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const setHour = (day: DayOfWeek, patch: Partial<DayHours>): void => {
    setForm((prev) => ({ ...prev, hours: { ...prev.hours, [day]: { ...prev.hours[day], ...patch } } }));
    setErrors((prev) => ({ ...prev, hours: undefined }));
  };

  const handleCep = async (raw: string): Promise<void> => {
    const cep = raw.replace(/\D/g, '').slice(0, 8);
    set('zipCode', cep);
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (!data.erro) {
        setForm((prev) => ({
          ...prev,
          street: data.logradouro || prev.street,
          district: data.bairro || prev.district,
          city: data.localidade || prev.city,
          state: data.uf || prev.state,
        }));
      }
    } catch {
      /* ignore lookup failures - the merchant can fill the address manually */
    } finally {
      setCepLoading(false);
    }
  };

  /**
   * Required-field validation for the step being shown.
   *
   * Documento, contato e responsável seguem OBRIGATÓRIOS aqui, e isso é
   * deliberado. O card 241 soltou esses mesmos campos no backoffice, onde a
   * operação cadastra empresas de terceiros a partir de informação pública e não
   * tem como saber o CNPJ nem o telefone. Aqui é o próprio dono se cadastrando:
   * sem e-mail e telefone não há login, cobrança nem contato, e o dado que falta
   * ninguém preenche depois.
   *
   * A relaxação é por SUPERFÍCIE, não por campo. Se um dia isto for
   * "padronizado" com o backoffice, a jornada do lojista passa a criar contas
   * sem meio de contato.
   */
  const validateStep = (index: number): FieldErrors => {
    const found: FieldErrors = {};
    if (index === 0) {
      if (!form.tradeName.trim()) found.tradeName = 'Informe o nome fantasia.';
      if (!form.name.trim()) found.name = 'Informe a razão social.';
      if (!form.document.trim()) found.document = 'Informe o CNPJ ou CPF.';
      if (!form.categoryId) found.categoryId = 'Escolha a categoria da loja.';
      if (!form.email.trim()) found.email = 'Informe o e-mail de contato.';
      else if (!EMAIL_RE.test(form.email.trim())) found.email = 'Informe um e-mail válido.';
      if (!form.phone.trim()) found.phone = 'Informe o telefone de contato.';
    }
    if (index === 1) {
      if (!form.responsibleName.trim()) found.responsibleName = 'Informe o nome do responsável.';
      if (!form.responsibleEmail.trim()) found.responsibleEmail = 'Informe o e-mail do responsável.';
      else if (!EMAIL_RE.test(form.responsibleEmail.trim()))
        found.responsibleEmail = 'Informe um e-mail válido.';
      if (!form.responsiblePhone.trim()) found.responsiblePhone = 'Informe o telefone do responsável.';
    }
    if (index === 2) {
      if (form.zipCode.replace(/\D/g, '').length !== 8) found.zipCode = 'Informe um CEP com 8 dígitos.';
      if (!form.street.trim()) found.street = 'Informe a rua.';
      if (!form.number.trim()) found.number = 'Informe o número.';
      if (!form.district.trim()) found.district = 'Informe o bairro.';
      if (!form.city.trim()) found.city = 'Informe a cidade.';
      if (!form.state) found.state = 'Escolha o estado.';
    }
    // `isWeekHoursComplete` já cobre faixas incompletas, invertidas e sobrepostas;
    // a mensagem aqui só aponta o passo, porque o erro exato aparece na faixa.
    if (index === 3 && !isWeekHoursComplete(form.hours)) {
      found.hours = 'Revise os horários destacados nos dias em que a loja abre.';
    }
    return found;
  };

  const goNext = (): void => {
    const found = validateStep(step);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setStep((current) => Math.min(current + 1, STEPS.length - 1));
  };

  const goBack = (): void => {
    setErrors({});
    setStep((current) => Math.max(current - 1, 0));
  };

  const buildPayload = (): MerchantOnboardingRequestDto => ({
    name: form.name.trim(),
    categoryId: form.categoryId,
    tradeName: form.tradeName.trim() || undefined,
    document: form.document || undefined,
    email: form.email.trim() || undefined,
    phone: form.phone || undefined,
    whatsapp: form.whatsapp || undefined,
    website: form.website.trim() || undefined,
    instagram: form.instagram.trim() || undefined,
    description: form.description.trim() || undefined,
    responsible: {
      name: form.responsibleName.trim() || undefined,
      role: form.responsibleRole.trim() || undefined,
      email: form.responsibleEmail.trim() || undefined,
      phone: form.responsiblePhone || undefined,
      document: form.responsibleDocument || undefined,
    },
    address: {
      zipCode: form.zipCode || undefined,
      street: form.street.trim() || undefined,
      number: form.number.trim() || undefined,
      complement: form.complement.trim() || undefined,
      district: form.district.trim() || undefined,
      city: form.city.trim() || undefined,
      state: form.state || undefined,
    },
    businessHours: weekHoursToPayload(form.hours),
  });

  const submit = async (): Promise<void> => {
    // Re-run every step's validation: the merchant can reach the review step
    // and then go back and clear a field.
    for (let index = 0; index < STEPS.length; index += 1) {
      const found = validateStep(index);
      if (Object.keys(found).length > 0) {
        setErrors(found);
        setStep(index);
        return;
      }
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      setResult(await onboardingApi.applyMerchant(buildPayload()));
    } catch (err) {
      setSubmitError(
        extractErrorMessage(
          err,
          'Não foi possível enviar o cadastro agora. Tente novamente em instantes.',
          SUBMIT_ERROR_MESSAGES
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <Screen>
        <Shell>
          <Intro>
            <Logo height={40} />
          </Intro>
          <Panel>
            <Success>
              <CheckCircle2 size={54} />
              <h2>Cadastro enviado!</h2>
              <p>{result.message}</p>
              <p>
                Sua solicitação entrou na fila de aprovação do time Kardume. Assim que for aprovada,
                você recebe no e-mail <strong>{form.email}</strong> os dados de acesso ao painel da
                Administração.
              </p>
              <code>Protocolo: {result.merchantId}</code>
              <Button variant="outline" onClick={() => window.location.assign('/')}>
                Voltar para o início
              </Button>
            </Success>
          </Panel>
        </Shell>
      </Screen>
    );
  }

  return (
    <Screen>
      <Shell>
        <Intro>
          <Logo height={40} />
          <h1>Cadastre sua loja no Kardume Urbano</h1>
          <p>
            Preencha os dados abaixo em poucos minutos. Nosso time revisa a solicitação e libera o
            acesso ao painel da Administração - você não precisa de conta para começar.
          </p>
        </Intro>

        <Panel>
          <Stepper steps={STEPS} currentIndex={step} onStepClick={setStep} />

          {step === 0 && (
            <StepBody>
              <SectionTitle>
                <Building2 size={18} /> Dados da loja
              </SectionTitle>
              <Grid2>
                <TextInput
                  label="Nome fantasia"
                  required
                  value={form.tradeName}
                  error={errors.tradeName}
                  placeholder="Ex.: Padaria do Bairro"
                  onChange={(e) => set('tradeName', e.target.value)}
                />
                <TextInput
                  label="Razão social"
                  required
                  value={form.name}
                  error={errors.name}
                  placeholder="Ex.: Padaria do Bairro LTDA"
                  onChange={(e) => set('name', e.target.value)}
                />
              </Grid2>
              <DocGrid>
                <SearchableComboBox
                  label="Tipo de documento"
                  items={DOC_TYPE_OPTIONS}
                  valueId={form.documentType}
                  onChange={(id) => set('documentType', id as DocumentType)}
                />
                <DocumentInput
                  label="CNPJ ou CPF"
                  required
                  value={form.document}
                  error={errors.document}
                  onChange={(value) => set('document', value)}
                />
              </DocGrid>
              <SearchableComboBox
                label="Categoria"
                required
                placeholder={categoriesLoading ? 'Carregando categorias...' : 'Selecione a categoria'}
                items={categoryOptions}
                valueId={form.categoryId || undefined}
                loading={categoriesLoading}
                error={errors.categoryId}
                onChange={(id) => set('categoryId', id)}
              />
              <RichTextInput
                label="Descrição"
                value={form.description}
                placeholder="Conte o que a sua loja oferece, diferenciais, etc."
                onChange={(html) => set('description', html)}
              />

              <SectionTitle>
                <Phone size={18} /> Contato
              </SectionTitle>
              <Grid2>
                <TextInput
                  label="E-mail"
                  type="email"
                  required
                  value={form.email}
                  error={errors.email}
                  placeholder="contato@sualoja.com.br"
                  onChange={(e) => set('email', e.target.value)}
                />
                <PhoneInput
                  label="Telefone"
                  required
                  value={form.phone}
                  error={errors.phone}
                  onChange={(value) => set('phone', value)}
                />
              </Grid2>
              <Grid2>
                <PhoneInput
                  label="WhatsApp"
                  value={form.whatsapp}
                  onChange={(value) => set('whatsapp', value)}
                />
                <TextInput
                  label="Instagram"
                  value={form.instagram}
                  placeholder="@sualoja"
                  onChange={(e) => set('instagram', e.target.value)}
                />
              </Grid2>
              <TextInput
                label="Site"
                value={form.website}
                placeholder="https://"
                onChange={(e) => set('website', e.target.value)}
              />
            </StepBody>
          )}

          {step === 1 && (
            <StepBody>
              <SectionTitle>
                <User size={18} /> Responsável pela loja
              </SectionTitle>
              <Hint>
                Quem o time Kardume procura para falar sobre o cadastro, o plano e o acesso ao painel.
              </Hint>
              <Grid2>
                <TextInput
                  label="Nome do responsável"
                  required
                  value={form.responsibleName}
                  error={errors.responsibleName}
                  onChange={(e) => set('responsibleName', e.target.value)}
                />
                <TextInput
                  label="Cargo"
                  value={form.responsibleRole}
                  placeholder="Ex.: Proprietário"
                  onChange={(e) => set('responsibleRole', e.target.value)}
                />
              </Grid2>
              <Grid2>
                <TextInput
                  label="E-mail do responsável"
                  type="email"
                  required
                  value={form.responsibleEmail}
                  error={errors.responsibleEmail}
                  onChange={(e) => set('responsibleEmail', e.target.value)}
                />
                <PhoneInput
                  label="Telefone do responsável"
                  required
                  value={form.responsiblePhone}
                  error={errors.responsiblePhone}
                  onChange={(value) => set('responsiblePhone', value)}
                />
              </Grid2>
              <DocumentInput
                label="CPF do responsável"
                value={form.responsibleDocument}
                onChange={(value) => set('responsibleDocument', value)}
              />
            </StepBody>
          )}

          {step === 2 && (
            <StepBody>
              <SectionTitle>
                <MapPin size={18} /> Endereço da loja
              </SectionTitle>
              <Grid2>
                <TextInput
                  label={cepLoading ? 'CEP (buscando...)' : 'CEP'}
                  required
                  value={maskCep(form.zipCode)}
                  error={errors.zipCode}
                  placeholder="00000-000"
                  onChange={(e) => handleCep(e.target.value)}
                />
                <TextInput
                  label="Rua"
                  required
                  value={form.street}
                  error={errors.street}
                  onChange={(e) => set('street', e.target.value)}
                />
              </Grid2>
              <Grid2>
                <TextInput
                  label="Número"
                  required
                  value={form.number}
                  error={errors.number}
                  onChange={(e) => set('number', e.target.value)}
                />
                <TextInput
                  label="Complemento"
                  value={form.complement}
                  placeholder="Ex.: Sala 101"
                  onChange={(e) => set('complement', e.target.value)}
                />
              </Grid2>
              <Grid3>
                <TextInput
                  label="Bairro"
                  required
                  value={form.district}
                  error={errors.district}
                  onChange={(e) => set('district', e.target.value)}
                />
                <TextInput
                  label="Cidade"
                  required
                  value={form.city}
                  error={errors.city}
                  onChange={(e) => set('city', e.target.value)}
                />
                <SearchableComboBox
                  label="Estado (UF)"
                  required
                  items={UF_OPTIONS}
                  valueId={form.state || undefined}
                  error={errors.state}
                  onChange={(id) => set('state', id)}
                />
              </Grid3>
            </StepBody>
          )}

          {step === 3 && (
            <StepBody>
              <SectionTitle>
                <Clock size={18} /> Horários de atendimento
              </SectionTitle>
              <Hint>
                Desligue os dias em que a loja não abre e use "Adicionar horário" quando ela fechar no
                meio do dia (almoço, por exemplo). Você pode ajustar isso depois no painel.
              </Hint>
              <BusinessHoursInput value={form.hours} onChange={setHour} />
              {errors.hours && <ErrorText>{errors.hours}</ErrorText>}
            </StepBody>
          )}

          {step === 4 && (
            <StepBody>
              <SectionTitle>
                <Sparkles size={18} /> Plano comercial
              </SectionTitle>
              {/*
                O core ainda não expõe uma vitrine pública de planos comerciais de
                lojista: `/v1/backoffice/plans` exige ROLE_ADMIN e `/v1/pass/plans`
                é a vitrine do consumidor (Kardume Pass). O contrato público de
                onboarding (`MerchantOnboardingRequestDto`) também não tem campo de
                plano. Enquanto não existir esse endpoint, o passo é informativo e
                não bloqueia o envio.
              */}
              <InfoBox>
                <Sparkles size={22} />
                <div>
                  <strong>A escolha do plano acontece depois da aprovação</strong>
                  <span>
                    O time Kardume entra em contato com o responsável para apresentar os planos
                    disponíveis e ativar o que fizer mais sentido para a sua loja. Nada é cobrado
                    para enviar este cadastro.
                  </span>
                </div>
              </InfoBox>
            </StepBody>
          )}

          {step === 5 && (
            <StepBody>
              <SectionTitle>
                <CheckCircle2 size={18} /> Revise e envie
              </SectionTitle>
              <Hint>Confira os dados abaixo. Use os passos acima para corrigir o que for preciso.</Hint>

              <ReviewGroup>
                <SectionTitle>
                  <Building2 size={16} /> Loja
                </SectionTitle>
                <ReviewList>
                  <dt>Nome fantasia</dt>
                  <dd>{form.tradeName || '-'}</dd>
                  <dt>Razão social</dt>
                  <dd>{form.name || '-'}</dd>
                  <dt>{form.documentType}</dt>
                  <dd>{form.document || '-'}</dd>
                  <dt>Categoria</dt>
                  <dd>{categoryName}</dd>
                  <dt>E-mail</dt>
                  <dd>{form.email || '-'}</dd>
                  <dt>Telefone</dt>
                  <dd>{form.phone || '-'}</dd>
                  <dt>WhatsApp</dt>
                  <dd>{form.whatsapp || '-'}</dd>
                  <dt>Instagram</dt>
                  <dd>{form.instagram || '-'}</dd>
                  <dt>Site</dt>
                  <dd>{form.website || '-'}</dd>
                </ReviewList>
              </ReviewGroup>

              <ReviewGroup>
                <SectionTitle>
                  <User size={16} /> Responsável
                </SectionTitle>
                <ReviewList>
                  <dt>Nome</dt>
                  <dd>{form.responsibleName || '-'}</dd>
                  <dt>Cargo</dt>
                  <dd>{form.responsibleRole || '-'}</dd>
                  <dt>E-mail</dt>
                  <dd>{form.responsibleEmail || '-'}</dd>
                  <dt>Telefone</dt>
                  <dd>{form.responsiblePhone || '-'}</dd>
                  <dt>CPF</dt>
                  <dd>{form.responsibleDocument || '-'}</dd>
                </ReviewList>
              </ReviewGroup>

              <ReviewGroup>
                <SectionTitle>
                  <MapPin size={16} /> Endereço
                </SectionTitle>
                <ReviewList>
                  <dt>CEP</dt>
                  <dd>{maskCep(form.zipCode) || '-'}</dd>
                  <dt>Logradouro</dt>
                  <dd>
                    {[form.street, form.number, form.complement].filter(Boolean).join(', ') || '-'}
                  </dd>
                  <dt>Bairro</dt>
                  <dd>{form.district || '-'}</dd>
                  <dt>Cidade / UF</dt>
                  <dd>{[form.city, form.state].filter(Boolean).join(' / ') || '-'}</dd>
                </ReviewList>
              </ReviewGroup>

              <ReviewGroup>
                <SectionTitle>
                  <Clock size={16} /> Horários
                </SectionTitle>
                <ReviewList>
                  {WEEK_DAYS.map(({ key, label }) => {
                    const dayHours = form.hours[key];
                    return (
                      <React.Fragment key={key}>
                        <dt>{label}</dt>
                        <dd>{describeDayHours(dayHours)}</dd>
                      </React.Fragment>
                    );
                  })}
                </ReviewList>
              </ReviewGroup>

              {submitError && <ErrorText>{submitError}</ErrorText>}
            </StepBody>
          )}

          <Footer>
            <Button variant="ghost" onClick={goBack} disabled={step === 0 || submitting}>
              Voltar
            </Button>
            {step < STEPS.length - 1 ? (
              <Button variant="primary" onClick={goNext}>
                Próximo passo
              </Button>
            ) : (
              <Button variant="primary" onClick={submit} isLoading={submitting}>
                Enviar cadastro
              </Button>
            )}
          </Footer>
        </Panel>
      </Shell>
    </Screen>
  );
};

export default MerchantOnboardingPage;
